import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useDTalkAccess, useDTalkHistory, useTeamDTalk } from '@/hooks/usePerformance';
import { trpc } from '@/lib/trpc';
import { DTalkHeader } from './DTalkLayout';
import { cn } from '@/lib/utils';
import { usePagination } from '@/hooks/usePagination';
import { format } from 'date-fns';

const PICKER_PAGE_SIZE = 12;

const TYPE_STYLE: Record<string, { key: string; ring: string; dot: string }> = {
  validada:   { key: 'type_validada',   ring: 'border-foreground', dot: 'bg-foreground' },
  reapertura: { key: 'type_reapertura', ring: 'border-border',     dot: 'bg-muted-foreground' },
  borrador:   { key: 'type_borrador',   ring: 'border-border',     dot: 'bg-muted-foreground/60' },
};

/** Selector de persona cuando se entra al Histórico sin :email (antes era un dead-end). */
function HistoryPersonPicker() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: access } = useDTalkAccess();
  // People elige de toda la base: no hace falta pedir el equipo
  const { data: team } = useTeamDTalk(!!access && !access.canViewAll);
  // Solo se pide la vista People cuando el acceso lo permite (evita 403 de fondo)
  const { data: all } = trpc.performance.dtalk.listAllForPeople.useQuery(undefined, {
    enabled: !!access?.canViewAll,
    staleTime: 60_000,
  });

  const rows = (access?.canViewAll ? all?.rows : team?.rows) ?? team?.rows ?? [];

  // Búsqueda + paginado: con toda la nómina el listado se hacía eterno.
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r: any) =>
      `${r.employeeName} ${r.employeeLastName} ${r.employeeEmail}`.toLowerCase().includes(s));
  }, [rows, search]);

  const { pageItems: pageRows, footer: paginationFooter } = usePagination(filtered, PICKER_PAGE_SIZE, search);

  return (
    <div className="space-y-5">
      <DTalkHeader title={t('performance.dtalk.history.title')} />
      <Card>
        <CardContent className="pt-6">
          <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-semibold">{t('performance.dtalk.history.pick_person', 'Elegí una persona para ver su historial')}</p>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('performance.dtalk.people.search_placeholder')}
              className="h-8 w-full sm:w-64 text-sm"
            />
          </div>
          {filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {rows.length === 0 ? t('performance.dtalk.history.empty') : t('performance.dtalk.people.no_records')}
            </p>
          ) : (
            <>
              <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
                {pageRows.map((r: any) => (
                  <button
                    key={r.employeeEmail}
                    className="rounded-lg border px-3 py-2 text-left text-sm hover:border-teal-500 hover:bg-accent/40"
                    onClick={() => navigate(`/performance/dtalk/history/${encodeURIComponent(r.employeeEmail)}`)}
                  >
                    <span className="font-semibold">{r.employeeName} {r.employeeLastName}</span>
                    <span className="block text-xs text-muted-foreground">{r.employeeEmail}</span>
                  </button>
                ))}
              </div>
              {paginationFooter}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function DTalkHistoryTab() {
  const { t } = useTranslation();
  const { email: encoded } = useParams<{ email?: string }>();
  const email = encoded ? decodeURIComponent(encoded) : '';
  const { data, isLoading } = useDTalkHistory(email);

  if (!email) {
    return <HistoryPersonPicker />;
  }
  if (isLoading) return <div className="flex items-center justify-center h-64"><WinclapLoader size={48} className="text-foreground/25" /></div>;

  const snapshots = data?.snapshots ?? [];

  return (
    <div className="space-y-5">
      <DTalkHeader title={t('performance.dtalk.history.title')} cycleLabel={data?.cycle ?? undefined} />
      <Card><CardContent className="pt-6">
        <div className="mb-5">
          <p className="text-lg font-bold">{data?.employeeName || email}</p>
          <p className="text-sm text-teal-600">{[data?.role, data?.area].filter(Boolean).join(' · ')}</p>
        </div>

        {snapshots.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{t('performance.dtalk.history.empty')}</p>
        ) : (
          <div className="relative pl-8">
            <div className="absolute left-[11px] top-2 bottom-2 w-px bg-border" />
            <div className="space-y-4">
              {snapshots.map((s: any, i: number) => {
                const style = TYPE_STYLE[s.type] ?? TYPE_STYLE.borrador;
                return (
                  <div key={i} className="relative">
                    <div className={cn('absolute -left-8 top-4 h-4 w-4 rounded-full border-2 border-background', style.dot)} />
                    <div className={cn('rounded-xl border p-4', style.ring, s.type === 'validada' ? 'border-2' : '')}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-xs font-bold tracking-wide uppercase">{t(`performance.dtalk.history.${style.key}`)}</span>
                        <span className="text-xs text-muted-foreground">{safeDate(s.timestamp)}</span>
                      </div>
                      {s.aspiracionPrincipal && (
                        <p className="text-sm mt-2"><span className="font-semibold text-teal-600">{t('performance.dtalk.history.aspiration')}</span> {s.aspiracionPrincipal}</p>
                      )}
                      {s.conclusion && <p className="text-sm text-foreground/90 mt-1">{s.conclusion}</p>}
                      {s.reopenReason && <p className="text-xs text-muted-foreground mt-1">{s.reopenReason}</p>}
                      <p className="text-xs text-muted-foreground text-right mt-2">{t('performance.dtalk.history.by', { author: s.author || 'Clapper' })}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground text-center mt-5">{t('performance.dtalk.history.footer')}</p>
      </CardContent></Card>
    </div>
  );
}

function safeDate(ts: string): string {
  try { return format(new Date(ts), 'dd/MM/yyyy'); } catch { return ''; }
}
